/**
 * Executes the custom color fee plan against the cart.
 *
 * The model decides WHAT the cart should hold; this module makes it so. It
 * owns the one-run-at-a-time protocol, re-entrancy (its own mutations emit
 * cart events, which would otherwise re-trigger it forever), and the decision
 * to fail silently rather than block a customer.
 *
 * readCart, addLines, changeLine, refresh and log are all injected, so the
 * whole protocol is testable in node --test with no DOM and no network — the
 * arrangement opc-budget-gate.js already uses.
 *
 * NOTHING HERE MAY BLOCK CHECKOUT. Every failure path leaves the cart exactly
 * as it was, logs, and returns. The next trigger tries again.
 */
(function (root, factory) {
  'use strict';
  const isNode = typeof module !== 'undefined' && module.exports;
  const model = isNode ? require('./custom-color-model.js') : root.CustomColorModel;
  const constants = isNode
    ? require('./custom-color-constants.js')
    : root.CustomColorConstants;
  const api = factory(model, constants);
  if (isNode) {
    module.exports = api;
  } else {
    root.CustomColorReconciler = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (model, C) {
  'use strict';

  function createReconciler(deps) {
    const readCart = deps.readCart;
    const addLines = deps.addLines;
    const changeLine = deps.changeLine;
    const refresh = deps.refresh || function () { return Promise.resolve(); };
    const log = deps.log || function () {};
    const feeVariantId = deps.feeVariantId;

    let running = false;
    let pending = false;
    // Sticky: once a settle chain hits MAX_SETTLE_RUNS, the cart is in a state
    // this module cannot resolve (a sold-out fee variant, a rejected add).
    // Retrying on every subsequent cart event would hammer the cart API for as
    // long as the tab stays open, so schedule() short-circuits until reload.
    let gaveUp = false;
    // The serialized plan most recently applied, held at reconciler-instance
    // scope (not inside settle()) so it survives across the pending-chained
    // continuation at the bottom of schedule() — a new settle() call started
    // from there is still guarding against the SAME mutation, not a fresh
    // one. Cleared whenever a run computes an EMPTY plan: that is the signal
    // the cart has genuinely settled, and forgetting it there is required —
    // otherwise a legitimate later repeat of an old plan (e.g. a customer
    // deleting the fee line by hand) would be mistaken for the same stale
    // read forever and never re-applied.
    let lastKey = null;

    // A stable serialization of a plan, used to notice that the cart still
    // hasn't caught up with our own last mutation (see planKey below).
    function planKey(plan) {
      return JSON.stringify(plan);
    }

    /**
     * Apply one plan to the cart: removals (including quantity corrections),
     * then adds. Addressed by line key, which stays stable while indices
     * shift under a batch; issued before adds so freed cart-transform bytes
     * are available before any are spent.
     *
     * markMutated is called after each mutation actually resolves, so a run
     * that fails partway (e.g. removals succeed, the add rejects) still
     * reports that the cart was touched.
     */
    function applyPlan(plan, markMutated) {
      const removals = plan.change.reduce(function (chain, c) {
        return chain.then(function () {
          return changeLine(c.id, c.quantity).then(function () {
            markMutated();
          });
        });
      }, Promise.resolve());

      return removals.then(function () {
        if (!plan.add.length) return null;
        return addLines(plan.add).then(function () {
          markMutated();
        });
      });
    }

    /**
     * One pass: read, plan, apply.
     *
     * lastKey (instance-scoped, see above) is the serialized plan this
     * reconciler applied on the previous pass, from this settle() OR an
     * earlier one chained through schedule()'s pending flag. /cart.js can lag
     * one mutation behind (see opc-budget-gate.js for why that lag is real,
     * not hypothetical): if the freshly-read cart still produces the SAME
     * plan we just applied, the cart hasn't caught up yet — applying it again
     * would add the same fee twice. That is treated as settled, not retried.
     *
     * An EMPTY plan clears lastKey: the cart has genuinely caught up, so
     * nothing should be remembered to compare a future plan against.
     *
     * Resolves { status: 'done' | 'stalled' | 'error' | 'mutated' }.
     */
    function runOnce(markMutated) {
      return Promise.resolve()
        .then(function () {
          return readCart();
        })
        .then(function (cart) {
          const items = (cart && cart.items) || [];

          // A line that is custom but unnameable charges nothing, and would
          // otherwise do so in silence. See model.unnamedCustomLines.
          const unnamed = model.unnamedCustomLines(items);
          if (unnamed.length) {
            log(
              'custom-color: '
                + unnamed.length
                + ' line(s) marked custom color carry no readable color text, so no fee '
                + 'was charged for them. The options app is probably using a new label for '
                + 'that field. Properties seen: '
                + unnamed
                  .map(function (line) {
                    return Object.keys(line.properties || {}).join(', ');
                  })
                  .join(' | ')
            );
          }

          const plan = model.planFor(items, feeVariantId);
          if (!plan.add.length && !plan.change.length) {
            lastKey = null;
            return { status: 'done' };
          }

          const key = planKey(plan);
          if (key === lastKey) {
            log('custom-color: cart did not reflect the previous mutation; stopping rather than repeating it');
            return { status: 'stalled' };
          }

          return applyPlan(plan, markMutated).then(function () {
            lastKey = key;
            return { status: 'mutated' };
          });
        })
        .catch(function (error) {
          log('custom-color: reconcile failed, cart left as-is: ' + String((error && error.message) || error));
          return { status: 'error' };
        });
    }

    /**
     * Read the cart and report whether the invariant already holds, WITHOUT
     * mutating anything. Used to check a suspected give-up before latching it.
     *
     * Deliberately not runOnce(): runOnce applies whatever plan it computes,
     * so calling it to "check" would mutate the cart again. A read failure
     * reports false — unproven is not settled, and the sticky flag is the
     * safer place to land when the cart cannot be read at all.
     */
    function verifySettled() {
      return Promise.resolve()
        .then(function () {
          return readCart();
        })
        .then(function (cart) {
          const plan = model.planFor((cart && cart.items) || [], feeVariantId);
          return !plan.add.length && !plan.change.length;
        })
        .catch(function () {
          return false;
        });
    }

    /**
     * Run to a settled cart, then re-render once — but only if a mutation
     * actually reached the cart. A run that mutated nothing (nothing to do,
     * or the very first read already errored) has nothing for the page to
     * re-render.
     *
     * A pass that mutated the cart can legitimately need one more pass.
     * Beyond MAX_SETTLE_RUNS a plan that keeps changing without ever emptying
     * means something is wrong, and looping would hammer the cart API for
     * nothing — see the gaveUp flag on schedule().
     */
    function settle() {
      let anyMutationIssued = false;
      let runs = 0;

      function markMutated() {
        anyMutationIssued = true;
      }

      function step() {
        runs += 1;
        return runOnce(markMutated).then(function (result) {
          if (result.status !== 'mutated') return null;
          if (runs >= C.MAX_SETTLE_RUNS) {
            // The last pass MUTATED; it was never read back. Latching gaveUp
            // here would strand a cart that the final mutation had in fact
            // fixed — and gaveUp is sticky, so every custom-color door added
            // for the rest of the page session would get no fee. Silent
            // revenue leak, the failure direction this feature must not have.
            // Confirm the cart is genuinely unsettled before giving up.
            return verifySettled().then(function (settled) {
              if (settled) {
                lastKey = null;
                return null;
              }
              gaveUp = true;
              log('custom-color: gave up after ' + runs + ' passes; cart never settled');
              return null;
            });
          }
          return step();
        });
      }

      return step().then(function () {
        return anyMutationIssued ? refresh() : null;
      });
    }

    /**
     * Ask for a reconcile. Triggers arriving mid-run set a single run-again
     * flag rather than queueing N runs: a customer adding several doors quickly
     * would otherwise get overlapping runs, each reading a stale cart and each
     * adding the same fee.
     *
     * Note for callers (Task 5 wires the cart-event listener to this): the
     * promise returned while a run is already in flight resolves immediately,
     * before the queued run has actually happened. It signals "your trigger
     * was noted", not "the cart has been reconciled" — await isBusy() going
     * false, not this promise, if that distinction matters to the caller.
     */
    function schedule() {
      if (gaveUp) return Promise.resolve();
      if (running) {
        pending = true;
        return Promise.resolve();
      }
      running = true;
      return settle()
        .catch(function () {})
        .then(function () {
          running = false;
          if (!pending) return null;
          pending = false;
          return schedule();
        });
    }

    return {
      schedule: schedule,
      isBusy: function () {
        return running;
      },
    };
  }

  return { createReconciler: createReconciler };
});
